// Plan 21-06 (Phase 21 / INFRA-05 web side) — ws-ticket fetcher.
//
// POSTs /api/match/{matchId}/ws-ticket with the current bearer JWT in
// Authorization. Returns the ticket string for use in the WebSocket
// subprotocol's second token (`ticket.<value>`).
//
// Replaces the prior `bearer.<base64url(jwt)>` subprotocol pattern. The
// bearer JWT is now confined to the standard HTTPS Authorization header
// and no longer travels in the WebSocket upgrade request — Cloudflare's
// edge access logs no longer persist a long-lived session token (CR-04
// resolved at the protocol layer).
//
// Server contract (Plan 21-05):
//   - POST /api/match/{matchId}/ws-ticket
//   - Authorization: Bearer <jwt>
//   - Accept: application/vnd.runegambit.v1+json
//   - 200 → { ticket: string, expires_in: number }  (expires_in == 60)
//   - 401 → invalid/expired bearer
//   - 404 → match not found OR caller not a participant
//   - 5xx / network → transient; caller may retry by rebuilding the WS client
//
// Tickets are single-use; the WS upgrade DELETES the KV entry on consume
// (Plan 21-05 mintWsTicket/consumeWsTicket). Each (re)connect MUST mint a
// fresh ticket — never cache across reconnect attempts (T-21-33).

export class WsTicketAuthError extends Error {
  constructor(message = 'WS_TICKET_AUTH_FAILED') {
    super(message)
    this.name = 'WsTicketAuthError'
  }
}

export class WsTicketMatchError extends Error {
  constructor(message = 'WS_TICKET_MATCH_NOT_FOUND') {
    super(message)
    this.name = 'WsTicketMatchError'
  }
}

export class WsTicketServerError extends Error {
  constructor(message = 'WS_TICKET_SERVER_ERROR') {
    super(message)
    this.name = 'WsTicketServerError'
  }
}

// Re-exported for parity with the legacy ws-client.AuthClearedError class.
// The ws-client module keeps its own copy for backward compatibility with
// existing callers (CR-03 invariant — callsite import surface unchanged).
export class AuthClearedError extends Error {
  constructor(message = 'AUTH_CLEARED') {
    super(message)
    this.name = 'AuthClearedError'
  }
}

const DEFAULT_API_BASE = '' // empty → same-origin

export interface FetchWsTicketOptions {
  readonly apiBase?: string
  readonly fetchImpl?: typeof fetch
}

/**
 * Mint a fresh ws-ticket for the given matchId. Calls the bearer getter
 * once per invocation (CR-03 freshness invariant — never caches the JWT).
 *
 * Throws:
 *   - AuthClearedError    — getBearer() returned null (user logged out)
 *   - WsTicketAuthError   — server returned 401 (JWT invalid/expired)
 *   - WsTicketMatchError  — server returned 404 (match not found / non-participant)
 *   - WsTicketServerError — 5xx / network failure / malformed response
 */
export async function fetchWsTicket(
  matchId: string,
  getBearer: () => string | null,
  opts: FetchWsTicketOptions = {},
): Promise<string> {
  const bearer = getBearer()
  if (bearer === null) {
    throw new AuthClearedError()
  }
  const apiBase = (
    opts.apiBase ??
    import.meta.env?.VITE_API_BASE_URL ??
    DEFAULT_API_BASE
  ).replace(/\/+$/, '')
  const fetcher = opts.fetchImpl ?? fetch
  const url = `${apiBase}/api/match/${encodeURIComponent(matchId)}/ws-ticket`

  let res: Response
  try {
    res = await fetcher(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${bearer}`,
        Accept: 'application/vnd.runegambit.v1+json',
      },
    })
  } catch (err) {
    throw new WsTicketServerError(
      err instanceof Error ? err.message : 'fetch failed',
    )
  }

  if (res.status === 401) throw new WsTicketAuthError()
  if (res.status === 404) throw new WsTicketMatchError()
  if (!res.ok) throw new WsTicketServerError(`status ${res.status}`)

  let body: { ticket?: string; expires_in?: number }
  try {
    body = (await res.json()) as { ticket?: string; expires_in?: number }
  } catch {
    throw new WsTicketServerError('response not valid JSON')
  }
  // Server mint guarantees a base64url string with ≥256 bits of entropy
  // (Plan 21-05 mintWsTicket → 32 random bytes → 43+ base64url chars).
  // A short/missing ticket here means a malformed server response.
  if (typeof body.ticket !== 'string' || body.ticket.length < 32) {
    throw new WsTicketServerError('invalid ticket in response')
  }
  return body.ticket
}
